<template>
  <CreatorBillingCardModal
    v-model:open="cardModalOpen"
    @saved="handleCardSaved"
  />
  <CreatorContactSalesModal :open="contactSalesOpen" :plan="contactSalesTier" @close="contactSalesOpen = false" />

  <div class="space-y-8 pb-12">
    <section v-if="subscription?.cancelAtPeriodEnd" class="flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p class="font-semibold text-amber-900">Your Platforma account is scheduled for cancellation.</p>
        <p class="mt-1 text-sm text-amber-800">You retain access until {{ formatDate(cancelEffectiveAt) }}.</p>
      </div>
      <button class="shrink-0 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#2D8CFF] shadow-sm ring-1 ring-amber-300 hover:bg-amber-100 disabled:opacity-60" :disabled="restoring" @click="restoreCancellation">
        {{ restoring ? 'Restoring…' : 'Keep my account' }}
      </button>
    </section>

    <div v-if="notice" class="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{{ notice }}</div>
    <div v-if="errorMessage" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ errorMessage }}</div>

    <section class="relative overflow-hidden rounded-[32px] bg-gradient-to-r from-fuchsia-500 via-[#2D8CFF] to-emerald-400 px-5 pb-9 pt-8 shadow-lg sm:px-8 lg:px-10">
      <div class="pointer-events-none absolute inset-0 opacity-20" :style="{ backgroundImage: 'radial-gradient(circle, white 1.2px, transparent 1.2px)', backgroundSize: '20px 20px' }" />
      <div class="relative text-center text-white">
        <p class="text-sm font-semibold uppercase tracking-[0.18em] text-white/75">Platforma creator plans</p>
        <h1 class="mt-2 text-3xl font-bold sm:text-4xl">Choose the plan that grows with you.</h1>
        <p class="mx-auto mt-3 max-w-2xl text-sm text-white/85 sm:text-base">See your current plan at a glance, then upgrade or downgrade whenever your business changes.</p>
        <div v-if="hasBothCadences" class="mx-auto mt-6 inline-flex rounded-full border border-white/70 p-1 text-sm font-semibold">
          <button type="button" class="rounded-full px-5 py-2 transition" :class="billingPeriod === 'annual' ? 'bg-white text-[#2D8CFF]' : 'text-white'" @click="billingPeriod = 'annual'">Yearly</button>
          <button type="button" class="rounded-full px-5 py-2 transition" :class="billingPeriod === 'monthly' ? 'bg-white text-[#2D8CFF]' : 'text-white'" @click="billingPeriod = 'monthly'">Monthly</button>
        </div>
      </div>

      <div v-if="loading" class="relative mt-8 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <div v-for="n in 4" :key="n" class="h-[360px] animate-pulse rounded-xl bg-white/80" />
      </div>
      <div v-else-if="visiblePlanTiers.length" class="relative mt-8 grid items-stretch gap-2" :class="planGridClass">
        <article v-for="(tier, index) in visiblePlanTiers" :key="tier.id" class="relative flex min-h-[360px] flex-col rounded-xl border-b-4 bg-white px-5 pb-6 pt-9 text-center shadow-xl transition duration-200" :class="[planPalette(tier, index).border, tier.id === currentTier?.id ? 'z-10 -translate-y-2 ring-4 ring-white/90' : 'hover:-translate-y-1']">
          <span v-if="tier.id === currentTier?.id" class="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-zinc-900 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">Your current plan</span>
          <h2 class="text-lg font-bold uppercase" :class="planPalette(tier, index).text">{{ displayPlanName(tier) }}</h2>
          <p class="mt-1 min-h-[42px] text-sm text-zinc-600">{{ tier.description || planTagline(tier) }}</p>
          <div class="mt-5 flex min-h-[68px] items-center justify-center" :class="planPalette(tier, index).text">
            <img v-if="isContactSalesTier(tier)" :src="enterpriseIcon" alt="" class="h-[68px] w-auto object-contain" />
            <template v-else>
              <span v-if="tier.billingCadence !== 'free_forever'" class="mr-1 self-start pt-2 text-2xl font-semibold">{{ currencySymbol(tier.currencyCode) }}</span>
              <span class="text-5xl font-medium">{{ tier.billingCadence === 'free_forever' ? 'Free' : planCardPrice(tier) }}</span>
            </template>
          </div>
          <p class="mt-1 text-xs font-bold uppercase tracking-wide text-zinc-400">{{ isContactSalesTier(tier) ? 'tailored plan' : tier.billingCadence === 'free_forever' ? 'forever' : tier.billingCadence === 'annual' ? 'per month · billed annually' : 'per month' }}</p>
          <button type="button" class="mt-6 h-12 w-full rounded-lg text-sm font-bold text-white shadow-lg transition disabled:cursor-not-allowed" :class="tier.id === currentTier?.id || tier.id === pendingTier?.id ? 'bg-zinc-300 text-zinc-600 shadow-none' : planPalette(tier, index).button" :disabled="planBusy || tier.id === currentTier?.id || tier.id === pendingTier?.id" @click="openPlanChange(tier)">
            {{ planButtonLabel(tier) }}
          </button>
          <p v-if="tier.billingCadence !== 'free_forever' && !isContactSalesTier(tier)" class="mt-2 text-xs font-semibold" :class="planPalette(tier, index).text">In-app upgrades are billed immediately</p>
          <ul class="mt-5 flex-1 space-y-2 text-left text-sm text-zinc-500">
            <li v-for="entitlement in enabledEntitlements(tier).slice(0, 4)" :key="entitlement.featureKey" class="flex gap-2"><CheckIcon class="mt-0.5 h-4 w-4 shrink-0" :class="planPalette(tier, index).text" /><span>{{ entitlement.featureName }}</span></li>
            <li v-if="!enabledEntitlements(tier).length" class="text-center">Core Platforma access</li>
          </ul>
        </article>
      </div>
      <div v-else class="relative mx-auto mt-8 max-w-xl rounded-2xl border border-white/50 bg-white/95 px-6 py-8 text-center shadow-xl">
        <p class="font-semibold text-zinc-900">No creator plans are available yet.</p>
        <p class="mt-2 text-sm text-zinc-500">Platforma will display active global plans and any custom plan assigned specifically to your account here.</p>
      </div>
    </section>

    <section class="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
      <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p class="text-sm font-semibold uppercase tracking-wide text-[#2D8CFF]">Your Plan</p>
          <h2 class="mt-2 text-2xl font-semibold text-zinc-900">{{ currentTier?.name || 'Platforma Plan' }}</h2>
          <p class="mt-1 text-sm text-zinc-500">{{ currentTier?.description || 'Your creator account subscription.' }}</p>
          <div class="mt-4 flex flex-wrap items-center gap-2"><span class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass">{{ statusLabel }}</span><span v-if="subscription?.metadata?.grandfathered" class="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">Grandfathered account</span><span v-if="subscription?.trialEndsAt && subscription?.status === 'trialing'" class="text-sm text-zinc-500">Trial ends {{ formatDate(subscription.trialEndsAt) }}</span></div>
        </div>
        <div class="rounded-xl bg-zinc-50 px-5 py-4 lg:min-w-[260px]"><p class="text-sm font-medium text-zinc-500">{{ subscription?.cancelAtPeriodEnd ? 'Access through' : 'Next payment' }}</p><p class="mt-1 text-lg font-semibold text-zinc-900">{{ nextPaymentText }}</p><p v-if="pendingTier" class="mt-2 text-xs text-[#2D8CFF]">{{ pendingTier.name }} begins {{ formatDate(pendingEffectiveAt) }}.</p></div>
      </div>
    </section>

    <section class="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.55fr)] lg:items-center">
        <div>
        <div class="flex items-start justify-between gap-4">
          <div><h2 class="text-lg font-semibold text-zinc-900">Payment method</h2><p class="mt-1 text-sm text-zinc-500">Used for your Platforma creator subscription.</p></div>
          <button class="rounded-lg border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 sm:hidden" @click="openCardModal()">{{ activeBillingProfile?.hasPaymentMethod ? 'Update card' : 'Add card' }}</button>
        </div>
        <div class="group relative mt-6 aspect-[375/200] w-full max-w-[375px] overflow-hidden rounded-[14px] bg-zinc-900 shadow-sm ring-1 ring-black/5">
          <img :src="memberCardImage" alt="Platforma card on file" class="absolute inset-0 h-full w-full object-cover" />
          <div class="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/25" />
          <div class="pointer-events-none absolute inset-0 z-10 text-white transition duration-200 group-hover:opacity-20 group-focus-within:opacity-20">
            <p class="absolute inset-x-5 top-[52%] -translate-y-1/2 text-center text-[17px] font-semibold tracking-[0.17em] drop-shadow-sm sm:text-[18px]">•••• •••• •••• {{ activeBillingProfile?.lastFour || '----' }}</p>
            <div class="absolute inset-x-5 bottom-5 flex items-end justify-between gap-4 text-[12px] font-semibold drop-shadow-sm">
              <span class="min-w-0 flex-1 truncate uppercase tracking-wide">{{ creatorCardholderName }}</span>
              <span class="shrink-0 tracking-wide">{{ cardExpiry }}</span>
            </div>
            <template v-if="activeBillingProfile?.hasPaymentMethod">
              <img v-if="normalizedCardBrand === 'mastercard'" :src="mastercardLogo" alt="" class="absolute bottom-4 right-4 hidden w-11 opacity-95" />
              <span v-else class="absolute bottom-5 right-5 hidden text-sm font-black italic tracking-tight">{{ cardBrandWordmark }}</span>
            </template>
          </div>
          <button type="button" class="absolute inset-0 z-20 flex items-center justify-center bg-zinc-950/60 text-sm font-semibold text-white transition duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-blue-300" :class="activeBillingProfile?.hasPaymentMethod ? 'opacity-0 hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100' : 'opacity-100'" @click="openCardModal()">
            <span class="rounded-lg bg-[#2D8CFF] px-4 py-2.5 shadow-lg shadow-black/20">{{ activeBillingProfile?.hasPaymentMethod ? 'Change payment method' : 'Add payment method' }}</span>
          </button>
        </div>
        <p class="mt-3 text-xs text-zinc-400">Your full card number and security code are never stored by Platforma.</p>
        </div>
        <div class="rounded-xl bg-blue-50 p-5 text-sm leading-6 text-blue-900"><p class="font-semibold">Secure account billing</p><p class="mt-1 text-blue-800">Your saved payment method is used only after you select a paid plan or when an active paid subscription renews. Grandfathered Free Forever accounts are never charged automatically.</p></div>
      </div>
    </section>

    <section class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div class="border-b border-zinc-200 px-6 py-5"><h2 class="text-lg font-semibold text-zinc-900">Billing history</h2></div>
      <div class="overflow-x-auto"><table class="min-w-full divide-y divide-zinc-200 text-sm">
        <thead class="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500"><tr><th class="px-6 py-3">Item</th><th class="px-6 py-3">Date</th><th class="px-6 py-3">Amount</th><th class="px-6 py-3">Status</th></tr></thead>
        <tbody class="divide-y divide-zinc-100">
          <tr v-for="entry in billingHistoryItems" :key="entry.key"><td class="px-6 py-4"><p class="font-medium text-zinc-800">{{ entry.label }}</p><p v-if="entry.reference" class="mt-1 text-xs text-zinc-400">{{ entry.reference }}</p></td><td class="px-6 py-4 text-zinc-500">{{ formatDate(entry.createdAt) }}</td><td class="px-6 py-4 text-zinc-700">{{ formatMoney(entry.amountCents, entry.currencyCode) }}</td><td class="px-6 py-4"><span class="rounded-full px-2.5 py-1 text-xs font-semibold capitalize" :class="entry.status === 'paid' ? 'bg-green-100 text-green-700' : entry.status === 'refunded' ? 'bg-blue-100 text-blue-700' : entry.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'">{{ entry.status }}</span></td></tr>
          <tr v-if="!billingHistoryItems.length"><td colspan="4" class="px-6 py-10 text-center text-zinc-400">No billing history yet.</td></tr>
        </tbody>
      </table></div>
    </section>

    <section class="rounded-2xl border border-red-200 bg-white p-6 shadow-sm sm:p-8">
      <h2 class="text-lg font-semibold text-zinc-900">Danger zone</h2>
      <p class="mt-2 text-sm font-semibold text-red-600">Proceed with caution.</p>
      <p class="mt-2 max-w-3xl text-sm leading-6 text-zinc-500">Thinking about changing your plan? Schedule cancellation and keep access through the effective date.</p>
      <button type="button" class="mt-6 rounded-lg bg-red-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-60" :disabled="!subscription || subscription.cancelAtPeriodEnd || subscription.status === 'canceled'" @click="openCancellation">
        {{ subscription?.cancelAtPeriodEnd ? 'Cancellation scheduled' : subscription?.status === 'canceled' ? 'Account canceled' : 'Delete account' }}
      </button>
    </section>
  </div>

  <TransitionRoot as="template" :show="planChangeModalOpen">
    <Dialog as="div" class="fixed inset-0 z-[2400]" @close="closePlanChange">
      <div class="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm" />
      <div class="fixed inset-0 overflow-y-auto p-4"><div class="flex min-h-full items-center justify-center"><DialogPanel class="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white p-6 shadow-2xl">
        <div v-if="planBusy" class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/95 px-8 text-center backdrop-blur-sm" role="status" aria-live="assertive">
          <span class="h-11 w-11 animate-spin rounded-full border-4 border-blue-100 border-t-[#2D8CFF]" />
          <p class="mt-5 text-lg font-semibold text-zinc-900">{{ planProcessingTitle }}</p>
          <p class="mt-2 max-w-sm text-sm leading-6 text-zinc-500">Please keep this window open while Platforma confirms the payment and updates your account.</p>
        </div>
        <DialogTitle class="text-xl font-semibold text-zinc-900">{{ planChangeModalTitle }}</DialogTitle>
        <p class="mt-2 text-sm leading-6 text-zinc-600">Review exactly when this change takes effect and what will be charged before confirming.</p>

        <div v-if="planChangeQuote" class="mt-5 space-y-3 rounded-2xl bg-zinc-50 p-5">
          <div class="flex items-start justify-between gap-4">
            <div><p class="text-xs font-semibold uppercase tracking-wide text-zinc-400">New plan</p><p class="mt-1 font-semibold text-zinc-900">{{ planChangeTarget?.name }}</p></div>
            <p v-if="planChangeTarget?.billingCadence === 'free_forever'" class="text-right font-semibold text-emerald-600">Free forever</p>
            <p v-else class="text-right font-semibold text-zinc-900">{{ formatMoney(planChangeTarget?.priceCents, planChangeTarget?.currencyCode) }}<span class="block text-xs font-normal text-zinc-500">per {{ planChangeTarget?.billingCadence === 'annual' ? 'year' : 'month' }}</span></p>
          </div>
          <div class="border-t border-zinc-200 pt-3">
            <div class="flex items-center justify-between gap-4"><span class="text-sm text-zinc-600">Charge today</span><strong class="text-lg text-zinc-900">{{ formatMoney(planChangeQuote.amountCents, planChangeQuote.currencyCode) }}</strong></div>
            <div v-if="planChangeQuote.proration?.currentUnusedCreditCents" class="mt-2 flex items-center justify-between gap-4 text-sm"><span class="text-zinc-500">Unused-plan credit included</span><span class="font-medium text-emerald-600">−{{ formatMoney(planChangeQuote.proration.currentUnusedCreditCents, planChangeQuote.currencyCode) }}</span></div>
          </div>
          <p v-if="planChangeQuote.action === 'scheduled_change'" class="rounded-xl bg-blue-50 p-3 text-sm leading-6 text-blue-800">There is no charge today. Your current plan stays active through {{ formatDate(planChangeQuote.effectiveAt) }}, and the new plan begins then.</p>
          <p v-else class="rounded-xl bg-amber-50 p-3 text-sm leading-6 text-amber-800">This in-app change does not start a new free trial. The new plan becomes active after the payment succeeds.</p>
          <p v-if="planChangeQuote.nextRenewalAt && planChangeQuote.action !== 'scheduled_change'" class="text-xs text-zinc-500">The next full {{ planChangeTarget?.billingCadence === 'annual' ? 'annual' : 'monthly' }} renewal is {{ formatMoney(planChangeTarget?.priceCents, planChangeTarget?.currencyCode) }} on {{ formatDate(planChangeQuote.nextRenewalAt) }}.</p>
        </div>

        <p v-if="planChangeError" class="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ planChangeError }}</p>
        <div class="mt-6 flex justify-end gap-3">
          <button class="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50" :disabled="planBusy" @click="closePlanChange">Keep current plan</button>
          <button class="rounded-lg bg-[#2D8CFF] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#176fcc] disabled:opacity-50" :disabled="planBusy || !planChangeQuote" @click="confirmPlanChange">{{ planChangeConfirmLabel }}</button>
        </div>
      </DialogPanel></div></div>
    </Dialog>
  </TransitionRoot>

  <transition name="celebration-fade">
    <div v-if="upgradeCelebrationOpen" class="fixed inset-0 z-[2600] flex items-center justify-center bg-zinc-950/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="upgrade-celebration-title">
      <div class="relative w-full max-w-lg overflow-hidden rounded-[28px] bg-white p-8 text-center shadow-2xl sm:p-10">
        <span class="billing-confetti billing-confetti-one" /><span class="billing-confetti billing-confetti-two" /><span class="billing-confetti billing-confetti-three" /><span class="billing-confetti billing-confetti-four" />
        <div class="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-4xl font-bold text-emerald-600">✓</div>
        <p class="mt-6 text-xs font-bold uppercase tracking-[.18em] text-emerald-600">Upgrade complete</p>
        <h3 id="upgrade-celebration-title" class="mt-2 text-3xl font-extrabold tracking-tight text-zinc-950">Welcome to {{ upgradeCelebration.planName }}</h3>
        <p class="mx-auto mt-3 max-w-md text-sm leading-6 text-zinc-500">Your new creator features are active now<span v-if="upgradeCelebration.amountCents">, and {{ formatMoney(upgradeCelebration.amountCents, upgradeCelebration.currencyCode) }} was paid successfully</span>.</p>
        <button type="button" class="mt-7 w-full rounded-xl bg-[#2D8CFF] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#176fcc]" @click="upgradeCelebrationOpen = false">Start using my new plan</button>
      </div>
    </div>
  </transition>

  <TransitionRoot as="template" :show="cancelModalOpen">
    <Dialog as="div" class="fixed inset-0 z-[2400]" @close="closeCancellation">
      <div class="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm" />
      <div class="fixed inset-0 overflow-y-auto p-4"><div class="flex min-h-full items-center justify-center"><DialogPanel class="w-full max-w-2xl rounded-xl bg-white p-7 shadow-2xl sm:p-8">
        <DialogTitle class="text-xl font-semibold text-zinc-900">Cancel your account</DialogTitle>
        <p class="mt-6 inline-flex items-center gap-2 text-xl font-bold uppercase tracking-tight text-red-600"><ExclamationCircleIcon class="h-6 w-6" /> Warning</p>
        <p class="mt-5 text-base leading-8 text-zinc-700">By canceling your account, you and your members will lose access to all accounts, and all membership data will be permanently deleted. <strong class="text-zinc-900">This operation cannot be undone.</strong></p>
        <p class="mt-3 text-base leading-8 text-zinc-700">Your current access continues only through <strong class="text-zinc-900">{{ formatDate(cancelEffectiveAt) }}</strong>, and no refunds are issued for the remaining period.</p>
        <p class="mt-9 text-xl font-semibold text-zinc-900">Do You Still Want To Cancel?</p>
        <label class="mt-5 flex select-none items-start gap-3">
          <input v-model="cancelConfirmed" type="checkbox" class="mt-1 h-6 w-6 rounded border-zinc-300 text-red-600 focus:ring-red-500">
          <span class="text-base leading-8 text-zinc-700">Yes, I have made the decision to quit my account. I understand that I will lose everything, and no refunds will be issued.</span>
        </label>
        <p v-if="cancelError" class="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ cancelError }}</p>
        <div class="mt-8 flex flex-wrap items-center justify-end gap-4"><button class="text-base font-semibold text-[#2D8CFF] hover:text-[#1d6ccc] disabled:opacity-60" :disabled="cancelSubmitting" @click="declineCancellation">I don't want to quit</button><button class="rounded-lg bg-red-500 px-5 py-2.5 text-base font-semibold text-white hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50" :disabled="cancelSubmitting || !cancelConfirmed" @click="submitCancellation">{{ cancelSubmitting ? 'Scheduling…' : 'Cancel my account' }}</button></div>
      </DialogPanel></div></div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from '@headlessui/vue';
import { CheckIcon } from '@heroicons/vue/20/solid';
import { ExclamationCircleIcon } from '@heroicons/vue/24/solid';
import CreatorBillingCardModal from '@/Components/CreatorBillingCardModal.vue';
import CreatorContactSalesModal from '@/Components/CreatorContactSalesModal.vue';
import enterpriseIcon from '@/assets/media/images/enterprise-icon.png';
import memberCardImage from '@/assets/media/images/member-cc.png';
import mastercardLogo from '@/assets/media/images/Mastercard-logo.png';

const apiBase = `${process.env.VUE_APP_API_PROXY_URL || ''}/api/platforma-creator-billing`;
const route = useRoute();
const router = useRouter();
const loading = ref(true);
const subscription = ref(null);
const planTiers = ref([]);
const pendingTier = ref(null);
const invoices = ref([]);
const microCharges = ref([]);
const billingProfiles = ref([]);
const activePaymentProvider = ref('stripe');
const errorMessage = ref('');
const notice = ref('');
const planBusy = ref(false);
const billingPeriod = ref('monthly');
const restoring = ref(false);
const cardModalOpen = ref(false);
const contactSalesOpen = ref(false);
const contactSalesTier = ref(null);
const pendingTierSelection = ref(null);
const planChangeModalOpen = ref(false);
const planChangeQuote = ref(null);
const planChangeTarget = ref(null);
const planChangeError = ref('');
const changeRequestId = ref('');
const cancelModalOpen = ref(false);
const cancelConfirmed = ref(false);
const cancelSubmitting = ref(false);
const cancelError = ref('');
const creatorCardholderName = ref('Platforma creator');
const upgradeCelebrationOpen = ref(false);
const upgradeCelebration = ref({ planName: 'your new plan', amountCents: 0, currencyCode: 'USD' });

const requestJson = async (path, options = {}) => {
  const response = await fetch(`${apiBase}${path}`, { credentials: 'include', cache: 'no-store', ...options });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) { const error = new Error(payload?.message || 'Billing request failed'); error.code = payload?.code; error.payload = payload; throw error; }
  return payload;
};
const applyPayload = (payload = {}) => {
  subscription.value = payload.subscription || null;
  if (payload.subscription?.tier?.billingCadence === 'annual') billingPeriod.value = 'annual';
  if (payload.subscription?.tier?.billingCadence === 'monthly') billingPeriod.value = 'monthly';
  pendingTier.value = payload.pendingTier || null;
  if (Array.isArray(payload.planTiers)) planTiers.value = payload.planTiers;
  if (Array.isArray(payload.invoices)) invoices.value = payload.invoices;
  if (Array.isArray(payload.microCharges)) microCharges.value = payload.microCharges;
  if (Array.isArray(payload.billingProfiles)) billingProfiles.value = payload.billingProfiles;
};
const loadOverview = async () => {
  loading.value = true;
  errorMessage.value = '';
  try {
    const [overview, processor] = await Promise.all([
      requestJson('/overview'),
      requestJson('/payment-provider'),
    ]);
    activePaymentProvider.value = processor.activeProvider === 'boa' ? 'boa' : 'stripe';
    applyPayload(overview);
  } catch (error) {
    errorMessage.value = error?.message || 'Unable to load billing details';
  } finally {
    loading.value = false;
  }
};
const currentTier = computed(() => subscription.value?.tier || null);
const activeBillingProfile = computed(() => billingProfiles.value.find((profile) => profile.provider === activePaymentProvider.value) || null);
const billingHistoryItems = computed(() => [
  ...invoices.value.map((invoice) => ({ ...invoice, key: `invoice-${invoice.id}`, label: invoice.invoicePublicId || 'Platforma subscription', reference: '' })),
  ...microCharges.value.map((charge) => ({ ...charge, key: `charge-${charge.chargePublicId}`, label: 'SMS Text Message broadcast', reference: charge.chargePublicId || '' })),
].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
const hasBothCadences = computed(() => planTiers.value.some((tier) => tier.billingCadence === 'monthly') && planTiers.value.some((tier) => tier.billingCadence === 'annual'));
const visiblePlanTiers = computed(() => {
  const regularTiers = planTiers.value.filter((tier) => !isContactSalesTier(tier)
    && (!hasBothCadences.value || tier.billingCadence === 'free_forever' || tier.billingCadence === billingPeriod.value));
  const contactTiers = planTiers.value.filter(isContactSalesTier);
  const cadenceContacts = hasBothCadences.value
    ? contactTiers.filter((tier) => tier.billingCadence === billingPeriod.value)
    : contactTiers;
  return [...regularTiers, ...(cadenceContacts.length ? cadenceContacts : contactTiers)];
});
const planGridClass = computed(() => {
  const count = visiblePlanTiers.value.length;
  if (count >= 4) return 'sm:grid-cols-2 xl:grid-cols-4';
  if (count === 3) return 'sm:grid-cols-2 xl:grid-cols-3';
  if (count === 2) return 'sm:grid-cols-2';
  return 'mx-auto max-w-sm grid-cols-1';
});
const cancelEffectiveAt = computed(() => subscription.value?.metadata?.cancelEffectiveAt || subscription.value?.currentPeriodEndsAt || subscription.value?.trialEndsAt || subscription.value?.nextBillingAt);
const pendingEffectiveAt = computed(() => subscription.value?.metadata?.pendingPlanChange?.effectiveAt);
const statusLabel = computed(() => subscription.value?.cancelAtPeriodEnd ? 'Cancelling' : ({ active: 'Active', trialing: 'Trial', past_due: 'Past due', canceled: 'Canceled', paused: 'Paused' }[subscription.value?.status] || 'Inactive'));
const statusClass = computed(() => ['Active', 'Trial'].includes(statusLabel.value) ? 'bg-green-100 text-green-700' : statusLabel.value === 'Cancelling' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700');
const cardExpiry = computed(() => activeBillingProfile.value?.expiryMonth && activeBillingProfile.value?.expiryYear ? `${String(activeBillingProfile.value.expiryMonth).padStart(2, '0')}/${String(activeBillingProfile.value.expiryYear).slice(-2)}` : '--/--');
const normalizedCardBrand = computed(() => {
  const brand = String(activeBillingProfile.value?.brand || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (brand === '002' || brand.includes('master')) return 'mastercard';
  if (brand === '001' || brand.includes('visa')) return 'visa';
  if (brand === '003' || brand.includes('amex') || brand.includes('americanexpress')) return 'amex';
  if (brand === '004' || brand.includes('discover')) return 'discover';
  return 'card';
});
const cardBrandWordmark = computed(() => ({ visa: 'VISA', amex: 'AMERICAN EXPRESS', discover: 'DISCOVER' }[normalizedCardBrand.value] || 'CARD'));
const planProcessingTitle = computed(() => Number(planChangeQuote.value?.amountCents || 0) > 0 ? 'Processing your payment…' : 'Updating your plan…');
const nextPaymentText = computed(() => {
  if (!currentTier.value || currentTier.value.billingCadence === 'free_forever') return 'No upcoming payment';
  if (subscription.value?.cancelAtPeriodEnd) return formatDate(cancelEffectiveAt.value);
  const date = subscription.value?.nextBillingAt || subscription.value?.trialEndsAt;
  return date ? `${formatMoney(currentTier.value.priceCents, currentTier.value.currencyCode)} on ${formatDate(date)}` : 'Scheduled after activation';
});
const formatMoney = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100);
const formatDate = (value) => { const date = value ? new Date(value) : null; return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'the end of your current access period'; };
const enabledEntitlements = (tier) => (tier?.entitlements || []).filter((item) => item.enabled);
const isContactSalesTier = (tier) => tier?.metadata?.planButtonAction === 'contact_sales';
const normalizedMonthlyPrice = (tier) => tier?.billingCadence === 'annual' ? Number(tier.priceCents || 0) / 12 : Number(tier?.priceCents || 0);
const displayPlanName = (tier) => {
  const name = String(tier?.name || 'Creator Plan').trim();
  if (tier?.billingCadence === 'free_forever' && !name.toLowerCase().includes('forever')) return `${name} Forever`;
  if (/^pro$/i.test(name)) return 'Professional';
  return name;
};
const planTagline = (tier) => {
  const name = String(tier?.name || '').toLowerCase();
  if (name.includes('enterprise')) return 'Best for large audiences';
  if (tier?.billingCadence === 'free_forever') return 'Best for getting started';
  return 'Built for growing creator businesses';
};
const currencySymbol = (currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 })
      .formatToParts(0).find((part) => part.type === 'currency')?.value || '$';
  } catch (_error) { return '$'; }
};
const wholePrice = (cents) => {
  const value = Math.max(0, Number(cents || 0)) / 100;
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};
const planCardPrice = (tier) => tier?.billingCadence === 'annual'
  ? String(Math.round(normalizedMonthlyPrice(tier) / 100))
  : wholePrice(tier?.priceCents);
const loadCreatorIdentity = async () => {
  try {
    const proxyBase = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '');
    const response = await fetch(`${proxyBase}/api/creator-details`, { credentials: 'include', cache: 'no-store' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return;
    const firstName = String(payload.creatorFirstName || payload.firstName || '').trim();
    const lastName = String(payload.creatorLastName || payload.lastName || '').trim();
    creatorCardholderName.value = [firstName, lastName].filter(Boolean).join(' ') || 'Platforma creator';
  } catch (_error) { creatorCardholderName.value = 'Platforma creator'; }
};
const planPalette = (tier, index = 0) => {
  const token = `${tier?.name || ''} ${tier?.slug || ''}`.toLowerCase();
  if (tier?.billingCadence === 'free_forever' || token.includes('free')) return { text: 'text-[#00b884]', border: 'border-[#00b884]', button: 'bg-[#00b884] shadow-[#00b884]/25 hover:bg-[#1B964F]' };
  if (token.includes('advanced')) return { text: 'text-[#49ccf9]', border: 'border-[#49ccf9]', button: 'bg-[#49ccf9] shadow-[#49ccf9]/30 hover:bg-[#1EB7EC]' };
  if (token.includes('enterprise')) return { text: 'text-[#292D34]', border: 'border-[#292D34]', button: 'bg-[#292D34] shadow-[#292D34]/20 hover:bg-black' };
  if (token.includes('pro') || index % 2 === 0) return { text: 'text-[#2D8CFF]', border: 'border-[#2D8CFF]', button: 'bg-[#2D8CFF] shadow-[#2D8CFF]/30 hover:bg-[#0f61c5]' };
  return { text: 'text-violet-600', border: 'border-violet-500', button: 'bg-violet-600 shadow-violet-500/25 hover:bg-violet-700' };
};
const planButtonLabel = (tier) => {
  if (tier.id === currentTier.value?.id) return 'Current plan';
  if (tier.id === pendingTier.value?.id) return 'Change scheduled';
  if (isContactSalesTier(tier)) return 'Contact Sales';
  return normalizedMonthlyPrice(tier) > normalizedMonthlyPrice(currentTier.value) ? 'Upgrade' : 'Downgrade';
};
const planChangeModalTitle = computed(() => {
  if (!planChangeQuote.value) return 'Review plan change';
  if (planChangeQuote.value.action === 'scheduled_change') return planChangeQuote.value.direction === 'downgrade' ? 'Schedule downgrade' : 'Schedule plan change';
  return `Upgrade to ${planChangeTarget.value?.name || 'new plan'}`;
});
const planChangeConfirmLabel = computed(() => {
  if (planBusy.value) return 'Processing…';
  if (!planChangeQuote.value) return 'Confirm change';
  if (planChangeQuote.value.action === 'scheduled_change') return 'Schedule plan change';
  if (Number(planChangeQuote.value.amountCents || 0) > 0) return `Pay ${formatMoney(planChangeQuote.value.amountCents, planChangeQuote.value.currencyCode)} & upgrade`;
  return 'Confirm plan change';
});
const emitUpdated = () => window.dispatchEvent(new CustomEvent('platforma:creator-subscription-updated'));
const openCardModal = () => { cardModalOpen.value = true; };
const newChangeRequestId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `change-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};
const openPlanChange = async (tier) => {
  if (planBusy.value) return;
  if (isContactSalesTier(tier)) {
    contactSalesTier.value = tier;
    contactSalesOpen.value = true;
    return;
  }
  planBusy.value = true; errorMessage.value = ''; notice.value = '';
  try {
    const payload = await requestJson('/plan/quote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tierId: tier.id }) });
    planChangeTarget.value = payload.targetTier || tier;
    planChangeQuote.value = payload.quote || null;
    planChangeError.value = '';
    changeRequestId.value = newChangeRequestId();
    planChangeModalOpen.value = true;
  } catch (error) {
    errorMessage.value = error?.message || 'Unable to prepare this plan change';
  } finally { planBusy.value = false; }
};
const openRequestedPlanChange = async () => {
  const requestedIdValue = Array.isArray(route.query.upgradePlanId) ? route.query.upgradePlanId[0] : route.query.upgradePlanId;
  const requestedSlugValue = Array.isArray(route.query.upgradePlanSlug) ? route.query.upgradePlanSlug[0] : route.query.upgradePlanSlug;
  const requestedId = Number.parseInt(String(requestedIdValue || ''), 10) || 0;
  const requestedSlug = String(requestedSlugValue || '').trim().toLowerCase();
  if (!requestedId && !requestedSlug) return;

  const requestedTier = planTiers.value.find((tier) => (
    (requestedId && Number(tier?.id || 0) === requestedId)
    || (requestedSlug && String(tier?.slug || '').trim().toLowerCase() === requestedSlug)
  ));

  try {
    if (!requestedTier) {
      errorMessage.value = 'The Advertising upgrade plan is not currently available.';
      return;
    }
    if (Number(requestedTier.id || 0) === Number(currentTier.value?.id || 0)) {
      notice.value = `You are already on ${requestedTier.name}. Advertising will unlock after the required successful plan payments.`;
      return;
    }
    await openPlanChange(requestedTier);
  } finally {
    const nextQuery = { ...route.query };
    delete nextQuery.upgradePlanId;
    delete nextQuery.upgradePlanSlug;
    delete nextQuery.upgradeSource;
    await router.replace({ path: route.path, query: nextQuery, hash: route.hash }).catch(() => null);
  }
};
const closePlanChange = () => { if (!planBusy.value) planChangeModalOpen.value = false; };
const confirmPlanChange = async () => {
  if (planBusy.value || !planChangeTarget.value || !planChangeQuote.value) return;
  if (planChangeQuote.value.requiresCharge === true && !activeBillingProfile.value?.hasPaymentMethod) {
    pendingTierSelection.value = planChangeTarget.value;
    planChangeModalOpen.value = false;
    openCardModal();
    return;
  }
  planBusy.value = true; planChangeError.value = ''; errorMessage.value = ''; notice.value = '';
  try {
    const payload = await requestJson('/plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tierId: planChangeTarget.value.id,
        confirmImmediateCharge: planChangeQuote.value.requiresCharge === true,
        maximumChargeCents: Number(planChangeQuote.value.amountCents || 0),
        changeRequestId: changeRequestId.value,
      }),
    });
    const outcome = payload.changeOutcome || planChangeQuote.value;
    const targetName = planChangeTarget.value.name;
    applyPayload(payload);
    planChangeModalOpen.value = false;
    if (outcome.action === 'scheduled_change') notice.value = `${targetName} is scheduled for ${formatDate(outcome.effectiveAt)}. There was no charge today.`;
    else if (payload.invoice?.status === 'paid') notice.value = `${formatMoney(payload.invoice.amountCents, payload.invoice.currencyCode)} was paid successfully. Your account is now on ${targetName}.`;
    else notice.value = `Your account is now on ${targetName}.`;
    await loadOverview(); emitUpdated();
    if (outcome.direction === 'upgrade' && outcome.action !== 'scheduled_change') {
      upgradeCelebration.value = { planName: targetName, amountCents: Number(payload.invoice?.amountCents || 0), currencyCode: payload.invoice?.currencyCode || outcome.currencyCode || 'USD' };
      upgradeCelebrationOpen.value = true;
    }
  } catch (error) {
    if (error?.code === 'payment_method_required') {
      pendingTierSelection.value = planChangeTarget.value;
      planChangeModalOpen.value = false;
      openCardModal();
    } else if (error?.code === 'plan_change_quote_updated' && error?.payload?.quote) {
      planChangeQuote.value = error.payload.quote;
      changeRequestId.value = newChangeRequestId();
      planChangeError.value = error?.message || 'The amount changed. Please review it again.';
    } else {
      if (error?.code === 'payment_failed' || error?.code === 'processor_not_available') changeRequestId.value = newChangeRequestId();
      planChangeError.value = error?.message || 'Unable to change plan';
    }
  } finally { planBusy.value = false; }
};
const handleCardSaved = async () => {
  await loadOverview();
  notice.value = 'Payment method updated.';
  if (pendingTierSelection.value) {
    const tier = pendingTierSelection.value;
    pendingTierSelection.value = null;
    await openPlanChange(tier);
  }
};
const trackCancellationInteraction = (action) => fetch(`${apiBase}/cancel/interaction`, {
  method: 'POST',
  credentials: 'include',
  keepalive: true,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ action }),
}).catch(() => null);
const openCancellation = () => {
  cancelConfirmed.value = false;
  cancelError.value = '';
  cancelModalOpen.value = true;
  void trackCancellationInteraction('modal_opened');
};
const closeCancellation = () => { if (!cancelSubmitting.value) cancelModalOpen.value = false; };
const declineCancellation = () => {
  if (cancelSubmitting.value) return;
  cancelModalOpen.value = false;
  void trackCancellationInteraction('chose_to_stay');
};
const submitCancellation = async () => {
  if (!cancelConfirmed.value || cancelSubmitting.value) return;
  cancelSubmitting.value = true; cancelError.value = '';
  try {
    applyPayload(await requestJson('/cancel', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirmation: 'CANCEL' }) }));
    cancelModalOpen.value = false; notice.value = 'Cancellation scheduled. You can reverse it from the banner before it takes effect.'; await loadOverview(); emitUpdated();
  } catch (error) { cancelError.value = error?.message || 'Unable to schedule cancellation'; } finally { cancelSubmitting.value = false; }
};
const restoreCancellation = async () => {
  if (restoring.value) return;
  restoring.value = true; errorMessage.value = '';
  try { applyPayload(await requestJson('/restore', { method: 'POST' })); notice.value = 'Your cancellation was reversed and your account will remain active.'; await loadOverview(); emitUpdated(); }
  catch (error) { errorMessage.value = error?.message || 'Unable to restore your account'; } finally { restoring.value = false; }
};

onMounted(async () => {
  await Promise.all([loadOverview(), loadCreatorIdentity()]);
  await openRequestedPlanChange();
});
</script>

<style scoped>
.celebration-fade-enter-active,.celebration-fade-leave-active{transition:opacity .2s ease}.celebration-fade-enter-from,.celebration-fade-leave-to{opacity:0}
.billing-confetti{position:absolute;height:28px;width:10px;border-radius:999px;animation:billing-confetti-float 1.5s ease-in-out infinite alternate}.billing-confetti-one{left:10%;top:16%;background:#2d8cff;transform:rotate(25deg)}.billing-confetti-two{right:11%;top:22%;background:#10b981;transform:rotate(-30deg);animation-delay:.2s}.billing-confetti-three{left:18%;bottom:12%;background:#f59e0b;transform:rotate(55deg);animation-delay:.4s}.billing-confetti-four{right:17%;bottom:15%;background:#a855f7;transform:rotate(-50deg);animation-delay:.3s}@keyframes billing-confetti-float{from{opacity:.35;translate:0 8px}to{opacity:1;translate:0 -14px}}
</style>
